import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { MessageSquareText, FileText, MessageCircle, ArrowRight, Clock } from "lucide-react";

export default function Dashboard() {
  const [stats, setStats] = useState({ conversations: 0, documents: 0, messages: 0 });
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([api.get("/dashboard/stats"), api.get("/conversations")]);
        setStats(s.data);
        setConvs(c.data.slice(0, 5));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const cards = [
    { label: "Conversations", value: stats.conversations, icon: MessageSquareText },
    { label: "Documents analysés", value: stats.documents, icon: FileText },
    { label: "Messages échangés", value: stats.messages, icon: MessageCircle },
  ];

  return (
    <div className="p-5 sm:p-10 max-w-5xl">
      <p className="text-xs uppercase tracking-[0.2em] font-semibold text-royal mb-2">Tableau de bord</p>
      <h1 className="font-serif text-4xl text-navy mb-8">Bienvenue 👋</h1>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
        {cards.map((c) => (
          <div key={c.label} data-testid={`stat-${c.label}`} className="bg-white border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-3">
              <c.icon size={20} className="text-royal" />
              <span className="font-serif text-3xl text-navy">{loading ? "–" : c.value}</span>
            </div>
            <p className="text-sm text-slate-500">{c.label}</p>
          </div>
        ))}
      </div>

      <button
        data-testid="dashboard-new-chat"
        onClick={() => navigate("/chat")}
        className="w-full bg-navy hover:bg-[#172A66] text-white rounded-lg p-6 flex items-center justify-between mb-10 transition-all duration-200"
      >
        <div className="text-left">
          <p className="font-serif text-2xl">Poser une nouvelle question</p>
          <p className="text-white/60 text-sm mt-1">Décrivez votre situation, l'assistant vous guide</p>
        </div>
        <ArrowRight size={24} />
      </button>

      <h2 className="font-serif text-2xl text-navy mb-4">Conversations récentes</h2>
      <div className="space-y-3">
        {!loading && convs.length === 0 && (
          <p className="text-slate-400 text-sm bg-white border border-border rounded-lg p-8 text-center">Aucune conversation pour le moment.</p>
        )}
        {convs.map((c) => (
          <button
            key={c.conversation_id}
            data-testid={`conv-${c.conversation_id}`}
            onClick={() => navigate(`/chat/${c.conversation_id}`)}
            className="w-full text-left bg-white border border-border rounded-lg p-5 hover:border-royal/40 hover:shadow-sm transition-all duration-200 flex items-start justify-between"
          >
            <div className="overflow-hidden pr-4">
              <p className="font-medium text-navy truncate">{c.title}</p>
              <p className="text-sm text-slate-500 truncate mt-1">{c.last_message || "Conversation vide"}</p>
            </div>
            <span className="text-xs text-slate-400 flex items-center gap-1 shrink-0">
              <Clock size={12} /> {new Date(c.updated_at).toLocaleDateString("fr-FR")}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
